/*=========================================================================================
  File Name: moduleAuthActions.js
  Description: Auth Module Actions

==========================================================================================*/

import jwt from "@/http/requests/auth/jwt/graphql";

import router from "@/router";
import profile_gql from "../../http/requests/inside/profile_gql";
import employee_gql from "../../http/requests/inside/employee_gql";

export default {
	// JWT
	loginJWT({ commit }, payload) {
		return new Promise((resolve, reject) => {
			jwt.login(payload.userDetails.email, payload.userDetails.password)
				.then(response => {
					// If there's user data in response
					if (response.user && response.user.token) {
						const token = response.user.token;

						// Set bearer token in request
						commit("SET_BEARER", token);

						// Update user details
						delete response.user.token;
						commit("UPDATE_USER_INFO", response.user, { root: true });

						if (response.permissions) {
							commit("SET_USER_PERMISSIONS", response.permissions);
						}

						resolve(response);

						// Navigate User to homepage
						let referrer = router.currentRoute.query.to || "/";
						if (referrer === "/pages/not-authorized") {
							referrer = "/";
						}
						router.push(referrer);
					} else {
						reject({ message: "WrongEmailOrPassword" });
					}
				})
				.catch(error => {
					reject(error);
				});
		});
	},
	registerUserJWT({ commit }, payload) {
		const { first_name, email, password, confirmPassword } = payload.userDetails;

		return new Promise((resolve, reject) => {
			// Check confirm password
			if (password !== confirmPassword) {
				reject({ message: "Password doesn't match. Please try again." });
			}

			jwt.registerUser(first_name, email, password)
				.then(response => {
					// Redirect User
					router.push(router.currentRoute.query.to || "/");

					// Set bearer token in request
					commit("SET_BEARER", response.data.token);

					commit("UPDATE_USER_INFO", response.data.userData, { root: true });

					resolve(response);
				})
				.catch(error => {
					reject(error);
				});
		});
	},
	fetchAccessToken() {
		return new Promise(resolve => {
			jwt.refreshToken().then(response => {
				resolve(response);
			});
		});
	},

	setUserPermissions({ commit }, permissions) {
		commit("SET_USER_PERMISSIONS", permissions);
	},

	fetchUserPermissions({ commit }, payload) {
		return new Promise((resolve, reject) => {
			jwt.loadPermissions(payload)
				.then(response => {
					if (response.permissions) {
						commit("SET_USER_PERMISSIONS", response.permissions);

						resolve(response);
					} else {
						reject({ message: "No permissions" });
					}
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	registerCompany(store, payload) {
		return new Promise((resolve, reject) => {
			jwt.registerCompany(payload)
				.then(response => {
					resolve(response);
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	updateProfile({ commit }, payload) {
		return new Promise((resolve, reject) => {
			profile_gql
				.updateUserInfo(payload)
				.then(response => {
					commit("UPDATE_USER_INFO", { role: response.role }, { root: true });

					resolve(response);
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	changePassword(store, payload) {
		return new Promise((resolve, reject) => {
			profile_gql
				.changePassword(payload)
				.then(response => {
					resolve(response);
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	fetchUserInfo({ commit }, userId) {
		return new Promise((resolve, reject) => {
			employee_gql
				.getEmployee(userId)
				.then(response => {
					if (response) {
						commit("UPDATE_USER_INFO", { role: response.employee }, { root: true });

						resolve(response);
					} else {
						reject({ message: "No Data" });
					}
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	fetchUserSettings({ commit }) {
		return new Promise((resolve, reject) => {
			profile_gql
				.fetchUserSettings()
				.then(response => {
					if (response) {
						const { content } = response.user_settings;
						commit("UPDATE_USER_SETTINGS", JSON.parse(content));

						resolve(JSON.parse(content));
					} else {
						reject({ message: "No Data" });
					}
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	fetchLocales({ commit }) {
		return new Promise((resolve, reject) => {
			profile_gql
				.fetchLocales()
				.then(response => {
					if (response) {
						commit("UPDATE_LOCALES", response.locales);

						resolve(response);
					} else {
						reject({ message: "No Data" });
					}
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	fetchTimezones({ commit }) {
		return new Promise((resolve, reject) => {
			profile_gql
				.fetchTimezones()
				.then(response => {
					if (response) {
						commit("UPDATE_TIMEZONES", response.timezones);

						resolve(response);
					} else {
						reject({ message: "No Data" });
					}
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	fetchDatetimeFormat({ commit }) {
		return new Promise((resolve, reject) => {
			profile_gql
				.fetchDatetimes()
				.then(response => {
					if (response) {
						commit("UPDATE_DATETIMEFORMAT", response.datetimes);
						resolve(response);
					} else {
						reject({ message: "No Data" });
					}
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	updateUserSettings({ commit }, payload) {
		return new Promise((resolve, reject) => {
			profile_gql
				.updateUserSettings(payload)
				.then(response => {
					if (response) {
						commit("UPDATE_USER_SETTINGS", JSON.parse(response.settings.content));
						resolve(JSON.parse(response.settings.content));
					} else {
						reject({ message: "No Data" });
					}
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	updateEmployeePoint({ commit }, payload) {
		return new Promise((resolve, reject) => {
			profile_gql
				.updateEmployeePoint(payload)
				.then(response => {
					const userInfo = JSON.parse(localStorage.getItem("userInfo"));
					const updatedEmployeePoints = userInfo.role.employee_points.map(point => {
						if (point.school_year === payload.school_year && point.semester === payload.semester) {
							return { ...point, ...response.employee_point };
						}
						return point;
					});

					commit("UPDATE_USER_INFO", {
						role: {
							...userInfo.role,
							employee_points: updatedEmployeePoints,
						}
					}, { root: true });

					resolve(response);
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	updateEmployeePoints({ commit }, payload) {
		return new Promise((resolve, reject) => {
			profile_gql
				.updateEmployeePoints(payload)
				.then(response => {
					commit("UPDATE_USER_INFO", {
						role: {
							...JSON.parse(localStorage.getItem("userInfo")).role,
							employee_points: response.employee_points,
						}
					}, { root: true } );

					resolve(response);
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	updateEmployee({ commit }, payload) {
		return new Promise((resolve, reject) => {
			profile_gql
				.updateEmployee(payload)
				.then(response => {
					commit("UPDATE_USER_INFO", {
						role:  {
							...JSON.parse(localStorage.getItem("userInfo")).role,
							properties: response.update_employee.properties,
						},
					}, { root: true } );

					resolve(response);
				})
				.catch(error => {
					reject(error);
				});
		});
	}
};
